import { AlertCircle, FileText, UploadCloud, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { forwardRef } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { FileUploadViewProps } from "./view-props";

const SPRING = { type: "spring", stiffness: 400, damping: 28 } as const;

/** Variante compacta: um botão (vazio) ou uma linha com o arquivo selecionado. */
export const FileUploadLite = forwardRef<HTMLDivElement, FileUploadViewProps>(
  function FileUploadLite({ state, label, disabled, className }, ref) {
    const {
      palette,
      selectedFile,
      validationError,
      inputProps,
      hoverHandlers,
      triggerFileInput,
      removeFile,
    } = state;

    return (
      <div
        ref={ref}
        className={cn("w-full", className)}
        {...hoverHandlers}
        title={selectedFile ? undefined : "Também aceita colar (Ctrl+V) um print"}
      >
        <input {...inputProps} />
        <div className="flex w-full">
          <AnimatePresence mode="wait" initial={false}>
            {selectedFile ? (
              <motion.div
                key="file"
                initial={{ opacity: 0, scale: 0.96, y: 4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96, y: -4 }}
                transition={SPRING}
                className={cn(
                  "group relative flex w-full min-w-0 items-center justify-between rounded-lg border px-2.5 py-2 shadow-sm transition-shadow duration-200 hover:shadow-md",
                  palette.liteBg,
                  palette.border,
                  palette.text,
                )}
              >
                <div
                  onClick={triggerFileInput}
                  className="flex min-w-0 flex-1 cursor-pointer select-none items-center gap-2.5"
                  title="Clique para alterar o arquivo"
                >
                  <motion.span
                    initial={{ scale: 0.5, rotate: -8 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={SPRING}
                    className="grid size-7 shrink-0 place-items-center rounded-md border border-border bg-card shadow-sm"
                  >
                    <FileText className={cn("h-4 w-4", palette.fileIcon)} />
                  </motion.span>
                  <span className="min-w-0 truncate text-sm font-medium">
                    {palette.label || selectedFile.name}
                  </span>
                </div>
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  transition={SPRING}
                  onClick={removeFile}
                  className={cn(
                    "ml-2 shrink-0 rounded-md p-1.5 transition-colors hover:bg-black/10 focus:outline-none dark:hover:bg-white/10",
                    "text-current opacity-60 hover:opacity-100",
                  )}
                  type="button"
                  title="Remover arquivo"
                >
                  <X className="h-4 w-4" />
                </motion.button>
              </motion.div>
            ) : (
              <motion.div
                key="empty"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={SPRING}
                className="w-full"
              >
                <motion.div className="w-full" whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      palette.button,
                      "group h-10 w-full justify-start px-4 shadow-sm transition-shadow duration-200 hover:shadow-md",
                    )}
                    disabled={disabled}
                    type="button"
                    onClick={triggerFileInput}
                  >
                    <UploadCloud
                      className={cn(
                        "mr-2 h-4 w-4 transition-transform duration-200 group-hover:-translate-y-0.5",
                        palette.icon,
                      )}
                    />
                    {label || "Carregar arquivo"}
                  </Button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <AnimatePresence>
          {validationError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="mt-1.5 flex items-center gap-1 overflow-hidden px-1 text-xs text-destructive"
            >
              <AlertCircle className="h-3 w-3 shrink-0" />
              {validationError}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);
